import { useState } from 'react';
import { useFormik } from 'formik';
import { Col, Row, Space } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import Button from 'components/new-ui/base/button/Button';
import CreateNewCustomerDrawer from 'views/customers/components/create-customer-drawer/CreateNewCustomerDrawer';
import CustomerSelect from 'components/new-ui/composed/selects/CustomerSelect';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import FormPhoneNumber from 'components/new-ui/composed/formPhoneNumber/FormPhoneNumber';
import { FormFields } from './types';
import { thirdPartyValidationSchema } from './validationSchema';
import useCustomers from 'hooks/useCustomers';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { addThirdPartyJob, updateThirdPartyJob } from 'store/manifests/client';
import { ThirdPartyJobResponse } from 'store/manifests/types';
import { Events } from 'utils/events.enum';
import { isBeValidationError } from 'utils/common';

type Props = {
    open: boolean;
    manifestId: string;
    onClose: () => void;
    thirdPartyJob?: ThirdPartyJobResponse;
};

const AddThirdPartyJobModal = ({ open, onClose, manifestId, thirdPartyJob }: Props) => {
    const isEditing = !!thirdPartyJob;
    const { listCustomers } = useCustomers();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [showCustomerDrawer, setShowCustomerDrawer] = useState(false);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            [FormFields.CUSTOMER_ID]: thirdPartyJob?.customerId ?? undefined,
            [FormFields.JOB_REFERENCE_ID]: thirdPartyJob?.jobReferenceId ?? '',
            [FormFields.MRNS]: (thirdPartyJob?.mrns?.length ? thirdPartyJob.mrns : ['']) as string[],
            [FormFields.CUSTOMS_BROKER_NAME]: thirdPartyJob?.customsBrokerName ?? '',
            [FormFields.CUSTOMS_BROKER_EMAIL]: thirdPartyJob?.customsBrokerEmail ?? '',
            [FormFields.CUSTOMS_BROKER_PHONE_CODE]: thirdPartyJob?.customsBrokerPhoneCountryCode ?? undefined,
            [FormFields.CUSTOMS_BROKER_PHONE_NUMBER]: thirdPartyJob?.customsBrokerPhoneNumber ?? '',
        },
        validationSchema: thirdPartyValidationSchema,
        validateOnChange: false,
        onSubmit: async (values, helpers) => {
            try {
                const {
                    customerId,
                    jobReferenceId,
                    mrns,
                    customsBrokerName,
                    customsBrokerEmail,
                    customsBrokerPhoneNumber,
                    customsBrokerPhoneCountryCode,
                } = values;

                // Remove undefined mrns from the array
                const filteredMrns = mrns.filter(Boolean);

                const payload = {
                    customerId,
                    jobReferenceId,
                    mrns: filteredMrns,
                    customsBrokerName,
                    customsBrokerEmail,
                    customsBrokerPhoneCountryCode,
                    customsBrokerPhoneNumber,
                };

                showGlobalOverlay({
                    type: 'LoadingOverlay',
                    payload: {
                        message: isEditing ? 'Saving changes...' : 'Adding job...',
                    },
                });

                if (isEditing) {
                    await updateThirdPartyJob(manifestId, thirdPartyJob.id, payload);
                } else {
                    await addThirdPartyJob(manifestId, payload);
                }

                Notification({
                    type: 'success',
                    messageTitle: isEditing ? 'Job updated' : 'Job added',
                    description: isEditing
                        ? 'Successfully updated 3rd-party job.'
                        : 'Successfully added job to manifest.',
                    placement: 'top',
                });

                window.EventBus.publish(Events.ADDED_THIRD_PARTY_JOB_TO_MANIFEST);

                helpers.resetForm();

                onClose();
            } catch (error) {
                if (isBeValidationError(error)) {
                    error.response!.data.subErrors.forEach(({ field, message }) => {
                        helpers.setFieldError(field, message);
                        helpers.setFieldTouched(field, true, false);
                    });
                } else {
                    const errorMessage = getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE;

                    Notification({
                        type: 'error',
                        messageTitle: 'Operation failed',
                        description: errorMessage,
                        placement: 'top',
                    });
                }
            } finally {
                hideGlobalOverlay();
            }
        },
    });

    const handleSubmit = () => formik.submitForm();

    const addMrn = () => formik.setFieldValue('mrns', [...formik.values.mrns, undefined]);

    const removeMrn = (index: number) =>
        formik.setFieldValue(
            'mrns',
            formik.values.mrns.filter((_, i) => i !== index)
        );

    return (
        <>
            <ConfirmationModal
                title={isEditing ? 'Edit 3rd-party Job' : 'Add 3rd-party Job'}
                confirmButtonText={isEditing ? 'Save Changes' : 'Add Job'}
                icon={<PlusOutlined />}
                maskClosable={false}
                width={700}
                open={open}
                onClose={onClose}
                onCancel={onClose}
                onConfirm={handleSubmit}
                confirmationButtonLoading={formik.isSubmitting}
                confirmationButtonDisabled={formik.dirty && !formik.isValid}
                content={
                    <form style={{ width: '100%', paddingInline: 16, paddingBottom: 16 }} onSubmit={formik.submitForm}>
                        <Space style={{ width: '100%' }} direction="vertical" size={12}>
                            <Row>
                                <Col span={8}>
                                    <Space size={6}>
                                        <LabelRequiredSymbol />
                                        <label>Customer:</label>
                                    </Space>
                                </Col>
                                <Col span={16}>
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'start' }}>
                                        <div style={{ flex: 1 }}>
                                            <CustomerSelect
                                                customerId={formik.values[FormFields.CUSTOMER_ID]}
                                                fieldMeta={formik.getFieldMeta(FormFields.CUSTOMER_ID)}
                                                fieldHelper={formik.getFieldHelpers(FormFields.CUSTOMER_ID)}
                                                onChange={(val) => formik.setFieldValue(FormFields.CUSTOMER_ID, val)}
                                                onClear={() => formik.setFieldValue(FormFields.CUSTOMER_ID, undefined)}
                                            />
                                        </div>
                                        <Button onClick={() => setShowCustomerDrawer(true)}>Create</Button>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={8}>
                                    <Space size={6}>
                                        <LabelRequiredSymbol />
                                        <label>Job Ref ID:</label>
                                    </Space>
                                </Col>
                                <Col span={16}>
                                    <FormInput
                                        fieldProps={formik.getFieldProps(FormFields.JOB_REFERENCE_ID)}
                                        fieldHelper={formik.getFieldHelpers(FormFields.JOB_REFERENCE_ID)}
                                        fieldMeta={formik.getFieldMeta(FormFields.JOB_REFERENCE_ID)}
                                        placeholder="Enter Job ID"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col span={8}>
                                    <label>MRN:</label>
                                </Col>
                                <Col span={16}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        {formik.values.mrns.map((_, index) => {
                                            const isLast = index === formik.values.mrns.length - 1;

                                            // Yup stores per-item errors (e.g. minLength) as string[],
                                            // and array-level errors (e.g. at-least-one test) as a plain string.
                                            // We must distinguish them because indexing a string gives individual
                                            // characters, which would split the message across rows.
                                            const perItemError = Array.isArray(formik.errors.mrns)
                                                ? formik.errors.mrns[index]
                                                : undefined;

                                            // Array-level error is only shown on the last row (the one with +).
                                            const arrayError =
                                                isLast && typeof formik.errors.mrns === 'string'
                                                    ? formik.errors.mrns
                                                    : undefined;

                                            // Per-item touched mirrors the per-item error shape.
                                            const perItemTouched = Array.isArray(formik.touched.mrns)
                                                ? !!formik.touched.mrns[index]
                                                : false;

                                            // Array-level touched: show after a submit attempt or once any MRN row has been blurred.
                                            const arrayTouched =
                                                isLast &&
                                                (formik.submitCount > 0 ||
                                                    (Array.isArray(formik.touched.mrns) &&
                                                        formik.touched.mrns.some(Boolean)));
                                            return (
                                                <div
                                                    key={index}
                                                    style={{ display: 'flex', gap: 8, alignItems: 'start' }}
                                                >
                                                    <div style={{ flex: 1 }}>
                                                        <FormInput
                                                            fieldMeta={{
                                                                value: formik.values.mrns[index],
                                                                error: perItemError || arrayError,
                                                                touched: perItemTouched || arrayTouched,
                                                                initialValue: '',
                                                                initialTouched: false,
                                                                initialError: '',
                                                            }}
                                                            fieldProps={formik.getFieldProps(`mrns[${index}]`)}
                                                            placeholder="Enter MRN"
                                                        />
                                                    </div>
                                                    {isLast ? (
                                                        <Button onClick={addMrn} icon={<PlusOutlined />} />
                                                    ) : (
                                                        <Button
                                                            onClick={() => removeMrn(index)}
                                                            icon={<DeleteOutlined />}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={8}>
                                    <Space size={6}>
                                        <label>Customs Broker Name:</label>
                                    </Space>
                                </Col>
                                <Col span={16}>
                                    <FormInput
                                        fieldProps={formik.getFieldProps(FormFields.CUSTOMS_BROKER_NAME)}
                                        placeholder="Enter Customs Broker Name"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col span={8}>
                                    <Space size={6}>
                                        <label>Customs Broker Email:</label>
                                    </Space>
                                </Col>
                                <Col span={16}>
                                    <FormInput
                                        fieldProps={formik.getFieldProps(FormFields.CUSTOMS_BROKER_EMAIL)}
                                        fieldMeta={formik.getFieldMeta(FormFields.CUSTOMS_BROKER_EMAIL)}
                                        fieldHelper={formik.getFieldHelpers(FormFields.CUSTOMS_BROKER_EMAIL)}
                                        placeholder="Enter Customs Broker Email"
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col span={8}>
                                    <Space size={6}>
                                        <label>Customs Broker Phone:</label>
                                    </Space>
                                </Col>
                                <Col span={16}>
                                    <FormPhoneNumber
                                        label={''}
                                        phoneNumberFieldProps={formik.getFieldProps(
                                            FormFields.CUSTOMS_BROKER_PHONE_NUMBER
                                        )}
                                        phoneNumberFieldMeta={formik.getFieldMeta(
                                            FormFields.CUSTOMS_BROKER_PHONE_NUMBER
                                        )}
                                        phoneNumberFieldHelpers={formik.getFieldHelpers(
                                            FormFields.CUSTOMS_BROKER_PHONE_NUMBER
                                        )}
                                        codeFieldProps={formik.getFieldProps(FormFields.CUSTOMS_BROKER_PHONE_CODE)}
                                        codeFieldMeta={formik.getFieldMeta(FormFields.CUSTOMS_BROKER_PHONE_CODE)}
                                        codeFieldHelpers={formik.getFieldHelpers(FormFields.CUSTOMS_BROKER_PHONE_CODE)}
                                    />
                                </Col>
                            </Row>
                        </Space>
                    </form>
                }
            />

            <CreateNewCustomerDrawer
                open={showCustomerDrawer}
                title="Create Customer"
                refreshCustomers={() => listCustomers({ size: 200 })}
                onClose={() => setShowCustomerDrawer(false)}
                handleOk={async (newCustomer) => {
                    await formik.setFieldValue(FormFields.CUSTOMER_ID, newCustomer?.id);
                    await formik.setFieldTouched(FormFields.CUSTOMER_ID, true, true);
                    setShowCustomerDrawer(false);
                    Notification({
                        type: 'success',
                        messageTitle: 'Customer Created',
                        description: 'Successfully created customer in our system.',
                    });
                }}
                handleCreateError={(err) => {
                    return Notification({
                        type: 'error',
                        messageTitle: 'Customer creation failed',
                        description: getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE,
                        placement: 'top',
                    });
                }}
            />
        </>
    );
};

export default AddThirdPartyJobModal;
